import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import useAiQuota from '@/hooks/useAiQuota';
import {
  User, MapPin, Calendar, Stethoscope, BookOpen, ExternalLink,
  Plus, Trash2, Save, Loader2, CheckCircle, XCircle, Clock, AlertCircle, ToggleRight, Star, Sparkles,
  Camera, Upload, Wallet, UserX, Crown, CreditCard, Download, Lock, AlertTriangle, Copy, Share2, MessageCircle,
  FileText, Image, Send, SpellCheck, Bell, BellOff, BarChart3, TrendingUp, Eye, EyeOff, Phone, Search,
  Newspaper, History, Check, X, Edit3, RefreshCw, ChevronDown, QrCode, Zap, Gift, Ticket, ClipboardList
} from 'lucide-react';
import BookingChat from '@/components/BookingChat';
import PinSettings from '@/components/dashboard/PinSettings';
import ProfilePublishedCelebration from '@/components/dashboard/ProfilePublishedCelebration';
import MonogramAvatar from '@/components/MonogramAvatar';
import VoiceMeter from '@/components/dashboard/VoiceMeter';
import ClinicSearchPicker from '@/components/ClinicSearchPicker';
import ClinicMap from '@/components/ClinicMap';
import HospitalAttachmentsSection from '@/components/dashboard/HospitalAttachmentsSection';
import {
  Field, ShareUrlBar, PushNotificationToggle, RedeemCouponCard,
  VoiceAuthenticityNotice, ChangePasswordSection,
  WhatsNewLaunchBanner, SetupBlocker, WebsiteScoreWidget,
  TierBadgeWithUpgrade, UpgradeBanner, SubscriptionWarning,
} from '@/components/dashboard/widgets/SharedWidgets';

export default function VisibilityAcceleratorTab({ slug }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    api.get('/doctor/visibility-checklist')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (key, completed) => {
    setToggling(key);
    try {
      if (completed) {
        await api.post('/doctor/visibility-checklist/uncomplete', { key });
      } else {
        await api.post('/doctor/visibility-checklist/complete', { key });
      }
      load();
    } catch {}
    setToggling('');
  };

  const copyUrl = () => {
    const url = `${window.location.origin}/dr/${slug}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) return <div className="flex justify-center py-20 text-slate-400"><Loader2 size={18} className="animate-spin" /></div>;
  if (!data) return <div className="text-center py-16 text-slate-500">Unable to load checklist.</div>;

  const { items, progress } = data;
  const autoItems = items.filter(i => i.type === 'auto');
  const doctorItems = items.filter(i => i.type === 'doctor_action');

  return (
    <div className="space-y-6" data-testid="visibility-accelerator-tab">
      {/* Header */}
      <div className="bg-gradient-to-br from-sky-50 via-white to-amber-50 rounded-2xl border border-sky-200 p-6 md:p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Zap size={20} className="text-amber-500" />
              <h2 className="text-xl font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Turbo Visibility Accelerator
              </h2>
            </div>
            <p className="text-sm text-slate-600 max-w-xl leading-relaxed">
              These actions maximize how fast search engines discover and rank your website.
              Complete the one-time tasks below — each one directly boosts your online visibility.
            </p>
          </div>
          <div className="flex-shrink-0 text-center">
            <div className="relative w-20 h-20">
              <svg className="w-20 h-20 -rotate-90" viewBox="0 0 84 84">
                <circle cx="42" cy="42" r="38" fill="none" stroke="#E2E8F0" strokeWidth="6" />
                <circle cx="42" cy="42" r="38" fill="none"
                  stroke={progress.percent === 100 ? '#10B981' : '#0284C7'} strokeWidth="6"
                  strokeLinecap="round"
                  strokeDasharray={2 * Math.PI * 38}
                  strokeDashoffset={2 * Math.PI * 38 - (progress.percent / 100) * 2 * Math.PI * 38}
                  style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-lg font-bold text-slate-900">{progress.percent}%</span>
              </div>
            </div>
            <p className="text-[10px] text-slate-500 mt-1">{progress.done}/{progress.total} done</p>
          </div>
        </div>
      </div>

      {/* Copy URL helper */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-sm font-medium text-slate-900">Your DocSite URL</p>
          <p className="text-xs text-slate-500 mt-0.5 font-mono">{window.location.origin}/dr/{slug}</p>
        </div>
        <button onClick={copyUrl}
          className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-sky-50 text-sky-700 text-sm font-medium hover:bg-sky-100 transition-colors"
          data-testid="copy-docsite-url">
          {copied ? <><CheckCircle size={14} /> Copied!</> : <><Copy size={14} /> Copy URL</>}
        </button>
      </div>

      {/* Auto actions — what DocSite does */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 md:p-6">
        <h3 className="text-sm font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
          What DocSite Does Automatically
        </h3>
        <p className="text-xs text-slate-500 mb-4">These run in the background — no action needed from you.</p>
        <div className="space-y-3">
          {autoItems.map(item => (
            <div key={item.key} className="flex items-start gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-100" data-testid={`visibility-${item.key}`}>
              <CheckCircle size={18} className="text-emerald-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-emerald-900">{item.title}</p>
                <p className="text-xs text-emerald-700 mt-0.5">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Doctor actions — what you need to do */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 md:p-6">
        <h3 className="text-sm font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
          What You Need to Do (Easy One-Time Tasks)
        </h3>
        <p className="text-xs text-slate-500 mb-4">Each task takes under 5 minutes and has a direct impact on your Google ranking.</p>
        <div className="space-y-3">
          {doctorItems.map(item => (
            <div key={item.key}
              className={`rounded-xl border p-4 transition-colors ${item.completed ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-200 hover:border-sky-200'}`}
              data-testid={`visibility-${item.key}`}>
              <div className="flex items-start gap-3">
                <button
                  onClick={() => toggle(item.key, item.completed)}
                  disabled={toggling === item.key}
                  className={`mt-0.5 flex-shrink-0 w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-colors ${
                    item.completed
                      ? 'bg-emerald-500 border-emerald-500 text-white'
                      : 'border-slate-300 hover:border-sky-400'
                  }`}
                  data-testid={`toggle-${item.key}`}>
                  {toggling === item.key ? <Loader2 size={12} className="animate-spin" /> : item.completed ? <Check size={14} /> : null}
                </button>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${item.completed ? 'text-slate-500 line-through' : 'text-slate-900'}`}>
                    {item.title}
                  </p>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">{item.description}</p>
                  {!item.completed && item.how_to && (
                    <div className="mt-3 bg-sky-50 border border-sky-100 rounded-lg p-3">
                      <p className="text-[10px] uppercase tracking-wider font-bold text-sky-600 mb-1.5">How to do it</p>
                      {item.how_to.split('\n').map((step, i) => (
                        <p key={i} className="text-xs text-sky-800 leading-relaxed">{step}</p>
                      ))}
                    </div>
                  )}
                  {item.completed && item.completed_at && item.completed_at !== 'Automatic' && (
                    <p className="text-[10px] text-emerald-600 mt-1">Completed {new Date(item.completed_at).toLocaleDateString()}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Timeline note */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-start gap-3" data-testid="visibility-timeline-note">
        <AlertCircle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-amber-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Good things take time
          </p>
          <p className="text-xs text-amber-800 mt-1 leading-relaxed">
            Search engines typically take 3-4 months to build credibility for new content, after which results accelerate significantly.
            The actions above ensure you're doing everything right from day one — so when Google gives you credibility, your content is already indexed, linked, and ready to rank.
          </p>
        </div>
      </div>

      {/* 5 Links Challenge */}
      <FiveLinksChallenge slug={slug} doctorName={data.doctor_name} doctorQualifications={data.doctor_qualifications} />
    </div>
  );
}

function FiveLinksChallenge({ slug, doctorName, doctorQualifications }) {
  const [challenge, setChallenge] = useState(null);
  const [copiedKey, setCopiedKey] = useState('');
  const [toggling, setToggling] = useState('');

  const siteUrl = `${window.location.origin}/dr/${slug}`;

  useEffect(() => {
    api.get('/doctor/link-challenge').then(r => setChallenge(r.data)).catch(() => {});
  }, []);

  const copyText = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(''), 2000);
    });
  };

  const toggleDone = async (key) => {
    setToggling(key);
    try {
      const isDone = challenge?.completed?.[key];
      if (isDone) {
        await api.post('/doctor/visibility-checklist/uncomplete', { key });
      } else {
        await api.post('/doctor/visibility-checklist/complete', { key });
      }
      const r = await api.get('/doctor/link-challenge');
      setChallenge(r.data);
    } catch {}
    setToggling('');
  };

  const sendWhatsAppKit = () => {
    const messages = [
      `*1. Google Business Profile*\nOpen Google → Search your clinic → Edit Profile → Website → Paste this:\n${siteUrl}`,
      `*2. Your Medical Portals*\nLogin to your registered medical portal → Edit Profile → Website field → Paste:\n${siteUrl}`,
      `*3. LinkedIn Post*\nCopy-paste this as a LinkedIn post:\n\nExcited to share my personal medical website — patients can now view my qualifications, procedures, and book appointments directly.\nVisit: ${siteUrl}`,
      `*4. WhatsApp Status*\nPost this as your WhatsApp status:\n\nMy website is live! View my practice details and book appointments directly: ${siteUrl}`,
      `*5. Email Signature*\nAdd this to your Gmail/email signature:\n\n${doctorName}${doctorQualifications ? ' | ' + doctorQualifications : ''} | Book Online: ${siteUrl}`,
    ].join('\n\n---\n\n');
    const fullText = `*DocSite — 5 Links, 5x Visibility*\n\nComplete these 5 simple tasks to boost your Google visibility:\n\n${messages}\n\n_Place your link on all 5 platforms and your patient visibility will grow at least 5x faster over the next 3 months._`;
    window.open(`https://wa.me/?text=${encodeURIComponent(fullText)}`, '_blank');
  };

  const LINK_CARDS = [
    {
      key: 'link_google_business',
      title: 'Google Business Profile',
      subtitle: 'Most Important — Highest Impact',
      instruction: 'Open Google → Search your clinic name → Click "Edit Profile" → Go to "Website" → Paste your link → Save',
      copyValue: siteUrl,
      copyLabel: 'Copy Your URL',
    },
    {
      key: 'link_medical_portals',
      title: 'Your Medical Portals',
      subtitle: 'Every portal where you are listed',
      description: 'Already listed on medical portals? Add your personal website link to your profile there. Patients who find you on those portals will visit your own website — where they see your full credentials, procedures, articles, and can book directly with you.',
      instruction: 'On your current profile registered medical portal, go to Edit Profile → "Website" field → Paste your link → Save. Do this on every portal where you are listed. Each one counts.',
      copyValue: siteUrl,
      copyLabel: 'Copy Your URL',
    },
    {
      key: 'link_linkedin',
      title: 'LinkedIn',
      subtitle: 'Post about your website',
      instruction: 'Open LinkedIn → New Post → Paste the text below → Post',
      copyValue: `Excited to share my personal medical website — patients can now view my qualifications, procedures, and book appointments directly. Visit: ${siteUrl}`,
      copyLabel: 'Copy Ready-Made Post',
    },
    {
      key: 'link_whatsapp',
      title: 'WhatsApp Status',
      subtitle: 'Share with your patient network',
      instruction: 'Open WhatsApp → Status → Text → Paste the message below → Post',
      copyValue: `My website is live! View my practice details and book appointments directly: ${siteUrl}`,
      copyLabel: 'Copy Message',
    },
    {
      key: 'link_email_signature',
      title: 'Email Signature',
      subtitle: 'Every email becomes a link to your website',
      instruction: 'Open Gmail → Settings → Signature → Paste the line below at the bottom → Save',
      copyValue: `${doctorName}${doctorQualifications ? ' | ' + doctorQualifications : ''} | Book Online: ${siteUrl}`,
      copyLabel: 'Copy Signature Line',
    },
  ];

  const done = challenge?.done || 0;

  return (
    <div className="space-y-4" data-testid="five-links-challenge">
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-50 via-white to-sky-50 rounded-2xl border border-emerald-200 p-6 md:p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp size={20} className="text-emerald-600" />
              <h2 className="text-xl font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                5 Links, 5x Visibility
              </h2>
            </div>
            <p className="text-sm text-slate-600 max-w-xl leading-relaxed">
              Get 5x more patient visibility in 3 months. Let's tell Google you're a real, practicing doctor. 10 minutes total. Results compound every month.
            </p>
          </div>
          <div className="flex-shrink-0 text-center">
            <div className="text-3xl font-bold text-emerald-600">{done}/5</div>
            <p className="text-[10px] text-slate-500">links placed</p>
          </div>
        </div>
        {/* Progress bar */}
        <div className="mt-4 h-2 bg-slate-200 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${(done / 5) * 100}%` }} />
        </div>
        {done < 5 && (
          <p className="text-xs text-emerald-700 mt-2 font-medium">{5 - done} more to go — each link accelerates your visibility</p>
        )}
        {done === 5 && (
          <p className="text-xs text-emerald-700 mt-2 font-medium">All 5 links placed! Your visibility is on the fast track.</p>
        )}
      </div>

      {/* Link Cards */}
      <div className="space-y-3">
        {LINK_CARDS.map(card => {
          const isDone = !!challenge?.completed?.[card.key];
          return (
            <div key={card.key}
              className={`rounded-2xl border p-5 transition-colors ${isDone ? 'bg-emerald-50/50 border-emerald-200' : 'bg-white border-slate-200'}`}
              data-testid={`link-card-${card.key}`}>
              <div className="flex items-start gap-3">
                <button onClick={() => toggleDone(card.key)} disabled={toggling === card.key}
                  className={`mt-0.5 flex-shrink-0 w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-colors ${
                    isDone ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300 hover:border-emerald-400'
                  }`}
                  data-testid={`link-toggle-${card.key}`}>
                  {toggling === card.key ? <Loader2 size={12} className="animate-spin" /> : isDone ? <Check size={14} /> : null}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`text-sm font-semibold ${isDone ? 'text-emerald-700 line-through' : 'text-slate-900'}`}>
                      {card.title}
                    </p>
                    {card.subtitle && (
                      <span className="text-[10px] font-medium text-sky-600 bg-sky-50 px-2 py-0.5 rounded-full">{card.subtitle}</span>
                    )}
                  </div>
                  {card.description && (
                    <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">{card.description}</p>
                  )}
                  {!isDone && (
                    <>
                      <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
                        <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Instructions</p>
                        <p className="text-xs text-slate-700 leading-relaxed">{card.instruction}</p>
                      </div>
                      <div className="mt-3 bg-sky-50 border border-sky-100 rounded-lg p-3">
                        <p className="text-[10px] uppercase tracking-wider font-bold text-sky-600 mb-2">Copy & Paste</p>
                        <p className="text-xs text-slate-800 font-mono break-all leading-relaxed mb-2">{card.copyValue}</p>
                        <button onClick={() => copyText(card.copyValue, card.key)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-600 text-white text-xs font-medium hover:bg-sky-700 transition-colors"
                          data-testid={`copy-${card.key}`}>
                          {copiedKey === card.key ? <><CheckCircle size={12} /> Copied!</> : <><Copy size={12} /> {card.copyLabel}</>}
                        </button>
                      </div>
                    </>
                  )}
                  {isDone && challenge?.completed?.[card.key] && (
                    <p className="text-[10px] text-emerald-600 mt-1">Completed {new Date(challenge.completed[card.key]).toLocaleDateString()}</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* WhatsApp Kit */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-sm font-medium text-slate-900">Send All 5 to Your WhatsApp</p>
          <p className="text-xs text-slate-500 mt-0.5">Get all 5 pre-written messages on your phone. Copy-paste at your convenience.</p>
        </div>
        <button onClick={sendWhatsAppKit}
          className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors"
          data-testid="send-whatsapp-kit">
          <MessageCircle size={16} /> Send to WhatsApp
        </button>
      </div>

      {/* Bottom note */}
      <p className="text-xs text-center text-slate-500 leading-relaxed py-2">
        Place your link on at least 5 platforms and your patient visibility will grow at least 5x faster over the next 3 months.
      </p>
    </div>
  );
}

